import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import * as action from '../Store/Pom.action';
import { Page_Size, checkArray, createOptions } from '../../../../Components/helper';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import * as systemMasterAction from '../../../SystemMasters/Store/SystemMasters.action';

export const StateContext = createContext(null);

/**
 * Builds the grading-group list fetch payload from the current listing state so every
 * refetch (filter, search, duplicate, status toggle, delete, infinite scroll) sends the
 * same active filters. Pass overrides for per-call values such as pageIndex or isScrolled.
 */
export function buildGradingListParams(state, loggedInUserId, overrides = {}) {
  const params = {
    pageIndex: state?.pageIndex,
    pageSize: state?.pageSize,
    loggedInUserId,
    isPubParam: 1,
    brandId: state?.selectGradingBrand,
    subBrandId: state?.selectSubBrand,
    catOneId: state?.selectCategoryOne,
    catTwoId: state?.selectCategoryTwo,
    catThreeId: state?.selectCategoryThree,
    status: state?.selectStatus,
  };
  if (state?.catBasedOnPTData && Array.isArray(state?.selectProductType) && state.selectProductType.length) {
    params.productTypeId = state.selectProductType;
  }
  if (state?.searchText) {
    params.searchText = state.searchText;
  }
  return { ...params, ...overrides };
}

export default function GradingGroupProvider({ children }) {
  const dispatch = useDispatch();
  const reducerState = useSelector((redState) => redState);
  const {
    PomDetails: { GradingListingData, totalListCount, BrandList, CategoryOneList, CategoryTwoList, CategoryThreeList },
    UserDetails: { userInfo },
    Users: { logInRights },
    SystemMastersDetails: { multiSubBrandList, subBrandAccess, pcTypeList },
  } = reducerState;
  const loggedInUserId = userInfo.userId;
  const { CheckActionPermission } = useComponent();
  const GradingAddEditAccess = CheckActionPermission(logInRights, 'libraries', 'pomGrading', 'addEdit');
  const GradingDeleteAccess = CheckActionPermission(logInRights, 'libraries', 'pomGrading', 'delete');

  const initialState = {
    isLoading: false,
    pageIndex: 1,
    searchText: '',
    pageSize: Page_Size,
    ListingData: [],
    hasMore: false,
    isPageLoader: false,
    selectedGrading: [],
    deleteModal: false,
    deleteMultipleModal: false,
    deletedId: [],
    searchLoaded: false,
    duplicateModal: false,
    duplicateData: null,
    BrandData: [],
    selectGradingBrand: [],
    CategoryOneData: [],
    selectCategoryOne: [],
    CategoryTwoData: [],
    selectCategoryTwo: [],
    CategoryThreeData: [],
    selectCategoryThree: [],
    isFilter: false,
    selectStatus: [],
    selectSubBrand: [],
    isSubBrandAccess: false,
    SubBrandData: [],
    // product type filter (flag ON only)
    ProductTypeData: [],
    selectProductType: [],
    catBasedOnPTData: false,
  };
  const [gradingListingState, setgradingListing] = useState({ ...initialState });
  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(systemMasterAction.getCatBasedOnPT());
        setgradingListing((prev) => ({
          ...prev,
          catBasedOnPTData: !!res,
        }));
      } catch (e) {
        setgradingListing((prev) => ({
          ...prev,
          catBasedOnPTData: false,
        }));
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);

  useEffect(() => {
    dispatch(systemMasterAction.getSubBrandAccess());
    setgradingListing((prev) => ({
      ...prev,
      isSubBrandAccess: subBrandAccess,
    }));
  }, [dispatch, subBrandAccess]);

  // Fetch Product Types and map options
  useEffect(() => {
    try {
      dispatch(
        systemMasterAction.getPCTypesDetails({
          loggedInUserId: userInfo?.userId,
          isActive: true,
          tier: 'Tier1',
        }),
      );
    } catch (e) {
      // no-op
    }
  }, [dispatch, userInfo?.userId]);

  useEffect(() => {
    setgradingListing((prev) => ({
      ...prev,
      ProductTypeData: createOptions(pcTypeList, 'name', '_id'),
    }));
  }, [pcTypeList]);
  useEffect(() => {
    const callFn = async () => {
      const localstorageFilter = JSON.parse(localStorage.getItem('GradingListFilter'));
      let reqObj = {};
      if (localstorageFilter) {
        reqObj = {
          pageIndex: 1,
          pageSize: gradingListingState?.pageSize,
          loggedInUserId,
          isPubParam: 1,
          brandId: localstorageFilter?.brandId,
          catOneId: localstorageFilter?.catOneId,
          catTwoId: localstorageFilter?.catTwoId,
          catThreeId: localstorageFilter?.catThreeId,
          status: localstorageFilter?.status,
          subBrandId: localstorageFilter?.subBrandId,
        };
        setgradingListing((prevState) => ({
          ...prevState,
          selectGradingBrand: localstorageFilter?.brandId,
          selectSubBrand: localstorageFilter?.subBrandId,
          selectCategoryOne: localstorageFilter?.catOneId,
          selectCategoryTwo: localstorageFilter?.catTwoId,
          selectCategoryThree: localstorageFilter?.catThreeId,
          selectStatus: localstorageFilter?.status,
          selectProductType: localstorageFilter?.productTypeId || [],
          isFilter: true,
        }));
      } else {
        reqObj = {
          pageIndex: gradingListingState?.pageIndex,
          pageSize: gradingListingState?.pageSize,
          loggedInUserId,
          isPubParam: 1,
          brandId: gradingListingState?.selectGradingBrand,
          subBrandId: gradingListingState?.selectSubBrand,
          catOneId: gradingListingState?.selectCategoryOne,
          catTwoId: gradingListingState?.selectCategoryTwo,
          catThreeId: gradingListingState?.selectCategoryThree,
          status: gradingListingState?.selectStatus,
        };
      }
      // If flag is ON and Product Type filter selected, include it in the request
      if (gradingListingState?.catBasedOnPTData) {
        const pt = localstorageFilter ? localstorageFilter?.productTypeId : gradingListingState?.selectProductType;
        if (Array.isArray(pt) && pt.length) {
          reqObj.productTypeId = pt;
        }
      }
      setgradingListing((prev) => ({
        ...prev,
        isLoading: true,
      }));
      const res = await dispatch(action.fetchGradingGroupData(reqObj));
      if (res) {
        setgradingListing((prev) => ({
          ...prev,
          isLoading: false,
        }));
      }
    };

    callFn();
  }, []);

  useEffect(() => {
    setgradingListing((prev) => ({
      ...prev,
      BrandData: createOptions(BrandList, 'name', '_id'),
      CategoryOneData: createOptions(CategoryOneList, 'name', '_id'),
      CategoryTwoData: createOptions(CategoryTwoList, 'name', '_id'),
      SubBrandData: createOptions(multiSubBrandList, 'name', '_id'),
    }));
  }, [BrandList, CategoryOneList, CategoryTwoList, multiSubBrandList]);

  useEffect(() => {
    setgradingListing((prev) => ({
      ...prev,
      CategoryThreeData: createOptions(CategoryThreeList, 'name', '_id'),
    }));
  }, [CategoryThreeList]);

  // Cascading for listing filters (flag ON): Cat1 depends on PT, Cat2 depends on Cat1, Cat3 depends on Cat1+Cat2
  useEffect(() => {
    if (!gradingListingState.catBasedOnPTData) {
      // Flag OFF: legacy full lists
      setgradingListing((prev) => ({
        ...prev,
        CategoryOneData: createOptions(CategoryOneList, 'name', '_id'),
        CategoryTwoData: createOptions(CategoryTwoList, 'name', '_id'),
        CategoryThreeData: createOptions(CategoryThreeList, 'name', '_id'),
      }));
      return;
    }

    const selectedPTIds = Array.isArray(gradingListingState.selectProductType) ? gradingListingState.selectProductType : [];
    const selectedC1Ids = Array.isArray(gradingListingState.selectCategoryOne) ? gradingListingState.selectCategoryOne : [];
    const selectedC2Ids = Array.isArray(gradingListingState.selectCategoryTwo) ? gradingListingState.selectCategoryTwo : [];

    const cat1Base = CategoryOneList || [];
    const cat1Filtered = !selectedPTIds.length
      ? cat1Base
      : cat1Base.filter((c1) => {
          const pts = c1?.productType || [];
          const arr = Array.isArray(pts) ? pts : [pts];
          const ids = arr.map((x) => x?._id || x).filter(Boolean);
          return ids.some((id) => selectedPTIds.includes(id));
        });

    const cat2Base = CategoryTwoList || [];
    const cat2Filtered = !selectedC1Ids.length
      ? cat2Base
      : cat2Base.filter((c2) => {
          const rel = c2?.categoryOneId || c2?.catOneIds || [];
          const arr = Array.isArray(rel) ? rel : [rel];
          const ids = arr.map((x) => x?._id || x).filter(Boolean);
          return ids.some((id) => selectedC1Ids.includes(id));
        });

    const cat3Base = CategoryThreeList || [];
    const cat3Filtered =
      !selectedC1Ids.length || !selectedC2Ids.length
        ? []
        : cat3Base.filter((c3) => {
            const r1 = c3?.categoryOneId || c3?.catOneIds || [];
            const r2 = c3?.categoryTwoId || c3?.catTwoIds || [];
            const a1 = (Array.isArray(r1) ? r1 : [r1]).map((x) => x?._id || x).filter(Boolean);
            const a2 = (Array.isArray(r2) ? r2 : [r2]).map((x) => x?._id || x).filter(Boolean);
            return a1.some((id) => selectedC1Ids.includes(id)) && a2.some((id) => selectedC2Ids.includes(id));
          });

    setgradingListing((prev) => ({
      ...prev,
      CategoryOneData: createOptions(cat1Filtered, 'name', '_id'),
      CategoryTwoData: createOptions(cat2Filtered, 'name', '_id'),
      CategoryThreeData: createOptions(cat3Filtered, 'name', '_id'),
    }));
  }, [
    gradingListingState.catBasedOnPTData,
    gradingListingState.selectProductType,
    gradingListingState.selectCategoryOne,
    gradingListingState.selectCategoryTwo,
    CategoryOneList,
    CategoryTwoList,
    CategoryThreeList,
  ]);

  // Clear dependents when parent is cleared (flag ON only)
  useEffect(() => {
    if (!gradingListingState.catBasedOnPTData) return;
    if (!gradingListingState.selectProductType || gradingListingState.selectProductType.length === 0) {
      setgradingListing((prev) => ({
        ...prev,
        selectCategoryOne: [],
        selectCategoryTwo: [],
        selectCategoryThree: [],
      }));
    }
  }, [gradingListingState.catBasedOnPTData, gradingListingState.selectProductType]);

  useEffect(() => {
    if (!gradingListingState.catBasedOnPTData) return;
    if (!gradingListingState.selectCategoryOne || gradingListingState.selectCategoryOne.length === 0) {
      setgradingListing((prev) => ({
        ...prev,
        selectCategoryTwo: [],
        selectCategoryThree: [],
      }));
    }
  }, [gradingListingState.catBasedOnPTData, gradingListingState.selectCategoryOne]);

  useEffect(() => {
    if (!gradingListingState.catBasedOnPTData) return;
    if (!gradingListingState.selectCategoryTwo || gradingListingState.selectCategoryTwo.length === 0) {
      setgradingListing((prev) => ({
        ...prev,
        selectCategoryThree: [],
      }));
    }
  }, [gradingListingState.catBasedOnPTData, gradingListingState.selectCategoryTwo]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      let res;
      if (gradingListingState.searchLoaded) {
        setgradingListing((debouncestate) => ({
          ...debouncestate,
          pageIndex: 1,
          hasMore: true,
          ListingData: [],
          isLoading: true,
        }));
        dispatch(action.clearReducer());
        const reqObj = buildGradingListParams(gradingListingState, loggedInUserId, { pageIndex: 1 });
        res = await dispatch(action.fetchGradingGroupData(reqObj));
      }
      if (res) {
        setgradingListing((debstate) => ({
          ...debstate,
          searchLoaded: false,
          isLoading: false,
        }));
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradingListingState.searchText, dispatch]);

  useEffect(() => {
    if (GradingListingData && !isEmpty(GradingListingData)) {
      setgradingListing((prev) => ({
        ...prev,
        ListingData: [...gradingListingState.ListingData, ...GradingListingData],
        hasMore: totalListCount > [...gradingListingState.ListingData, ...GradingListingData]?.length,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GradingListingData, totalListCount]);
  const isallChecked =
    checkArray(gradingListingState?.ListingData) && gradingListingState?.selectedGrading?.length === gradingListingState?.ListingData?.length;
  const handleCheckAll = useCallback(
    (e) => {
      if (e.target.checked) {
        const allIds =
          gradingListingState?.ListingData && checkArray(gradingListingState?.ListingData) && gradingListingState?.ListingData?.map((ele) => ele._id);
        if (allIds) {
          setgradingListing((prevState) => ({
            ...prevState,
            selectedGrading: allIds.filter((element) => element !== undefined),
          }));
        }
      } else {
        setgradingListing((prevState) => ({
          ...prevState,
          selectedGrading: [],
        }));
      }
    },
    [gradingListingState?.ListingData, setgradingListing],
  );

  const memoizedContextValue = useMemo(() => {
    return { gradingListingState, setgradingListing, GradingAddEditAccess, GradingDeleteAccess, loggedInUserId, isallChecked, handleCheckAll };
  }, [gradingListingState, setgradingListing, GradingAddEditAccess, GradingDeleteAccess, loggedInUserId, isallChecked, handleCheckAll]);

  return <StateContext.Provider value={memoizedContextValue}>{children}</StateContext.Provider>;
}

export function useGradingGroupListingState() {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the StateProvider');
  }
  return context;
}
